import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts, getBlogCategories, getBlogTags } from '../../services/blogService';
import { BlogPost, BlogCategory } from '../../types/blog';

/**
 * Blog list page component
 * Displays a list of blog posts with filtering options
 * 
 * @returns {JSX.Element} The rendered Blog list page
 */
const POSTS_PER_PAGE = 9;

const BlogListPage: React.FC = () => {
  // Scroll to top on load
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
      console.log('[BlogListPage] Scrolled to top on load');
    } catch (err) {
      console.error('[BlogListPage] Error in scroll to top:', err);
    }
  }, []);

  // State for blog posts and filters
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Reset to page 1 if filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedTag]);

  // Load blog data
  useEffect(() => {
    try {
      // Log component rendering
      console.log('Rendering Blog list page');
      
      // Fetch blog posts based on filters
      const fetchedPosts = getBlogPosts(selectedCategory, selectedTag);
      setPosts(fetchedPosts);
      
      // Fetch categories and tags
      const fetchedCategories = getBlogCategories();
      const fetchedTags = getBlogTags();
      
      setCategories(fetchedCategories);
      setTags(fetchedTags);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading blog data:', error);
      setIsLoading(false);
    }
  }, [selectedCategory, selectedTag]);

  // Handle category filter change
  const handleCategoryChange = (categorySlug: string) => {
    setSelectedCategory(categorySlug === selectedCategory ? '' : categorySlug);
  };

  // Handle tag filter change
  const handleTagChange = (tag: string) => {
    setSelectedTag(tag === selectedTag ? '' : tag);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="relative overflow-hidden">
      {/* Decorative elements matching Hero section */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
        <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
      </div>
      
      {/* Subtle dot pattern overlay */}
      <div 
        className="absolute inset-0 opacity-10" 
        style={{
          backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
          backgroundSize: '12px 12px'
        }}
      ></div>
      
      {/* Hero section */}
      <div className="relative">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Raidu Blog
          </h1>
          <p className="mt-4 text-lg leading-8 text-gray-600 max-w-3xl">
            Insights and perspectives on AI governance, security, and compliance for the enterprise.
          </p>
        </div>
      </div>

      {/* Blog content */}
      <div className="relative mx-auto max-w-7xl px-6 pb-16 lg:px-8">
        <div className="lg:flex lg:gap-x-12">
          {/* Main content */}
          <div className="lg:w-3/4">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : posts.length > 0 ? (
              <>
                <div className="space-y-12">
                  {(() => {
                    const start = (currentPage - 1) * POSTS_PER_PAGE;
                    const paginated = posts.slice(start, start + POSTS_PER_PAGE);
                    return paginated.map((post) => (
                      <article key={post.id} className="relative bg-white p-6 rounded-xl shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Post image */}
                          <div className="md:w-1/3">
                            <Link to={`/blog/${post.slug}`} className="block overflow-hidden rounded-lg">
                              <img
                                src={'/blog-posters/'+post.slug+'.png'}
                                alt={post.title} 
                                className="h-48 w-full object-cover transition-transform duration-300 hover:scale-105"
                              />
                            </Link>
                          </div>
                          {/* Post content */}
                          <div className="md:w-2/3">
                            <div className="flex items-center gap-x-4 text-xs mb-2">
                              <time dateTime={post.date} className="text-gray-500">{formatDate(post.date)}</time>
                              <Link 
                                to={`/blog/category/${post.category}`}
                                className="relative z-10 rounded-full bg-indigo-50 px-3 py-1.5 font-medium text-indigo-600 hover:bg-indigo-100"
                              >
                                {categories.find(cat => cat.id === post.category)?.name || post.category}
                              </Link>
                            </div>
                            <div className="group">
                              <h2 className="text-xl font-bold tracking-tight text-gray-900 group-hover:text-indigo-600">
                                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                              </h2>
                              <p className="mt-2 text-gray-600 line-clamp-3">{post.excerpt}</p>
                            </div>
                            <div className="mt-4 flex items-center gap-x-4">
                              <div className="flex items-center gap-x-2">
                                <img src={post.author.avatar} alt={post.author.name} className="h-8 w-8 rounded-full bg-gray-100" />
                                <div className="text-sm">
                                  <p className="font-semibold text-gray-900">{post.author.name}</p>
                                  <p className="text-gray-500">{post.author.title}</p>
                                </div>
                              </div>
                              <div className="text-sm text-gray-500">{post.readTime} min read</div>
                            </div>
                          </div>
                        </div>
                      </article>
                    ));
                  })()}
                </div>
                {/* Pagination controls */}
                <div className="flex justify-center mt-12 gap-2">
                  {(() => {
                    const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
                    const pageNumbers = Array.from({length: totalPages}, (_, i) => i+1);
                    return (
                      <>
                        <button
                          onClick={() => { try { setCurrentPage(p => Math.max(1, p-1)); console.log('[BlogListPage] Prev page'); } catch(e){console.error(e);} }}
                          disabled={currentPage === 1}
                          className="px-3 py-1 rounded border bg-white text-gray-700 disabled:opacity-50"
                        >Prev</button>
                        {pageNumbers.map(n => (
                          <button key={n}
                            onClick={() => { try { setCurrentPage(n); console.log('[BlogListPage] Go to page', n); } catch(e){console.error(e);} }}
                            className={`px-3 py-1 rounded border ${n===currentPage ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                          >{n}</button>
                        ))}
                        <button
                          onClick={() => { try { setCurrentPage(p => Math.min(totalPages, p+1)); console.log('[BlogListPage] Next page'); } catch(e){console.error(e);} }}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 rounded border bg-white text-gray-700 disabled:opacity-50"
                        >Next</button>
                      </>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900">No posts found</h3>
                <p className="mt-2 text-gray-600">Try adjusting your filters or check back later for new content.</p>
                <button 
                  onClick={() => { setSelectedCategory(''); setSelectedTag(''); }}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="mt-12 lg:mt-0 lg:w-1/4">
            <div className="sticky top-6">
              {/* Categories */}
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
                <ul className="space-y-2">
                  {categories.map((category) => (
                    <li key={category.id}>
                      <button
                        onClick={() => handleCategoryChange(category.id)}
                        className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${
                          selectedCategory === category.id 
                            ? 'bg-indigo-100 text-indigo-700 font-medium' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {category.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Tags */}
              <div className="mt-8 bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleTagChange(tag)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                        selectedTag === tag
                          ? 'bg-indigo-100 text-indigo-700 font-medium'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {tag.replace(/-/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogListPage;
